import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SkeletonColumn {
    /** Width of the skeleton cell (e.g. '120px', '80px', '60%') */
    width?: string;
    /** Whether to show a narrow pill shape (for status/chips) */
    pill?: boolean;
}

@Component({
    selector: 'app-skeleton-table',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="skeleton-table">
            <!-- Header row -->
            <div class="skeleton-header">
                <div
                    *ngFor="let col of columns"
                    class="skeleton-header-cell"
                    [style.width]="col.width || 'auto'"
                    [style.flex]="col.width ? 'none' : '1'"
                >
                    <div class="skeleton-bone header-bone"></div>
                </div>
            </div>

            <!-- Data rows -->
            <div
                *ngFor="let row of rowsArray; let i = index"
                class="skeleton-row"
            >
                <div
                    *ngFor="let col of columns; let j = index"
                    class="skeleton-cell"
                    [style.width]="col.width || 'auto'"
                    [style.flex]="col.width ? 'none' : '1'"
                >
                    <div
                        class="skeleton-bone"
                        [class.pill]="col.pill"
                        [style.width]="cellWidths[i][j] || '75%'"
                    ></div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .skeleton-table {
                width: 100%;
                overflow-x: auto;
            }

            .skeleton-header {
                display: flex;
                gap: 8px;
                padding: 12px 16px;
                border-bottom: 2px solid #e0e0e0;
            }

            .skeleton-header-cell {
                min-width: 50px;
            }

            .skeleton-row {
                display: flex;
                gap: 8px;
                padding: 14px 16px;
                border-bottom: 1px solid #f0f0f0;
            }

            .skeleton-row:last-child {
                border-bottom: none;
            }

            .skeleton-cell {
                min-width: 50px;
                display: flex;
                align-items: center;
            }

            .skeleton-bone {
                height: 14px;
                width: 75%;
                background: linear-gradient(
                    90deg,
                    #e8e8e8 25%,
                    #f5f5f5 50%,
                    #e8e8e8 75%
                );
                background-size: 200% 100%;
                animation: shimmer 1.5s ease-in-out infinite;
                border-radius: 4px;
            }

            .skeleton-bone.header-bone {
                height: 12px;
                width: 60%;
                background: linear-gradient(
                    90deg,
                    #d8d8d8 25%,
                    #ececec 50%,
                    #d8d8d8 75%
                );
                background-size: 200% 100%;
                animation: shimmer 1.5s ease-in-out infinite;
            }

            .skeleton-bone.pill {
                height: 24px;
                border-radius: 12px;
                width: 70px;
            }

            @keyframes shimmer {
                0% {
                    background-position: 200% 0;
                }
                100% {
                    background-position: -200% 0;
                }
            }
        `,
    ],
})
export class SkeletonTableComponent implements OnChanges {
    /** Column definitions for the skeleton */
    @Input() columns: SkeletonColumn[] = [];

    /** Number of placeholder rows to display */
    @Input() rows = 10;

    rowsArray: number[] = [];
    cellWidths: string[][] = [];

    ngOnChanges(): void {
        this.rowsArray = Array.from({ length: this.rows }, (_, i) => i);
        this.cellWidths = this.rowsArray.map(() =>
            this.columns.map((col) =>
                col.pill ? '70px' : `${55 + Math.random() * 30}%`
            )
        );
    }
}
